import React, { useState, useCallback, useMemo } from "react";
import { useAppSelector, useAppDispatch } from "../../../../store/hooks";
import { formatNumber } from "../../../../utils/formatting";
import { Computer, ComputerModel, ComputerOption } from "../../../../types/computer";
import { COMPUTERS, COMPUTER_OPTIONS } from "../../../../constants/computer";
import { setPrimaryComputer, setBackupComputer } from "../../../../store/features/shipBuilderSlice";
import { setSectionComputer, setSectionBackupComputer } from "../../../../store/features/breakawayHullSlice";
import { formatCurrency } from "../../../../utils/formatting";

interface BreakawayComputerConfigProps {
  isOpen: boolean;
  onClose: () => void;
  inline?: boolean; // If true, render inline instead of as modal
}

interface SectionComputer {
  sectionId: string;
  primary: Computer | null;
  backup: Computer | null;
  primaryOptions: ComputerOption[];
  backupOptions: ComputerOption[];
  isAdequate: boolean;
}

export const BreakawayComputerConfig: React.FC<BreakawayComputerConfigProps> = ({
  isOpen,
  onClose,
  inline = false,
}) => {
  const dispatch = useAppDispatch();
  const breakawayHull = useAppSelector((state) => state.breakawayHull);
  const currentComputer = useAppSelector((state) => state.shipBuilder.computer);
  // const techLevel = useAppSelector((state) => state.shipBuilder.techLevel); // Available for future use
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [computerMode, setComputerMode] = useState<'single' | 'per-section'>('per-section');
  
  // Get section computers from Redux state (like main ComputerSelector)
  const sectionComputers: Record<string, SectionComputer> = useMemo(() => {
    const computers: Record<string, SectionComputer> = {};
    if (breakawayHull?.sections) {
      breakawayHull.sections.forEach(section => {
        if (section.computer || section.backupComputer) {
          computers[section.id] = {
            sectionId: section.id,
            primary: section.computer,
            backup: section.backupComputer,
            primaryOptions: section.computer?.options || [],
            backupOptions: section.backupComputer?.options || [],
            isAdequate: true, // Assume adequate if computer is configured
          };
        }
      });
    }
    return computers;
  }, [breakawayHull?.sections]);


  const handlePrimaryModelChange = (sectionId: string, model: ComputerModel) => {
    if (!breakawayHull?.sections) return;

    const section = breakawayHull.sections.find(s => s.id === sectionId);
    if (!section) return;

    const computerDef = COMPUTERS[model];
    const currentSection = sectionComputers[sectionId];
    const currentOptions = currentSection?.primaryOptions || [];

    // Check if new primary computer is smaller than existing backup computer
    if (currentSection?.backup) {
      const newPrimaryProcessing = computerDef.processing;
      const backupProcessing = currentSection.backup.processing;

      if (newPrimaryProcessing <= backupProcessing) {
        setErrors(prev => ({ 
          ...prev, 
          [sectionId]: `Primary computer (${newPrimaryProcessing} processing) must be larger than backup computer (${backupProcessing} processing)` 
        }));
        return;
      }
    }

    // Immediately update Redux state (like main ComputerSelector)
    const newPrimary: Computer = {
      model,
      type: computerDef.type,
      processing: computerDef.processing,
      techLevel: computerDef.techLevel,
      cost: computerDef.baseCost,
      options: currentOptions,
      isPrimary: true,
      isBackup: false,
    };

    dispatch(setSectionComputer({ sectionId, computer: newPrimary }));

    // Clear error for this section
    setErrors(prev => ({ ...prev, [sectionId]: '' }));
  };

  const handleBackupModelChange = (sectionId: string, model: ComputerModel | "") => {
    if (!breakawayHull?.sections) return;

    const section = breakawayHull.sections.find(s => s.id === sectionId);
    if (!section) return;

    if (model === "") {
      // No backup computer selected
      dispatch(setSectionBackupComputer({ sectionId, backupComputer: null }));
      // Clear error for this section
      setErrors(prev => ({ ...prev, [sectionId]: '' }));
    } else {
      // Validate backup computer against primary computer
      const currentSection = sectionComputers[sectionId];
      if (!currentSection || !currentSection.primary) {
        setErrors(prev => ({ ...prev, [sectionId]: 'Primary computer must be selected before backup computer' }));
        return;
      }

      const computerDef = COMPUTERS[model];
      const primaryProcessing = currentSection.primary.processing;
      const backupProcessing = computerDef.processing;

      // Backup computer must be smaller than primary computer (Traveller rules)
      if (backupProcessing >= primaryProcessing) {
        setErrors(prev => ({ 
          ...prev, 
          [sectionId]: `Backup computer (${backupProcessing} processing) must be smaller than primary computer (${primaryProcessing} processing)` 
        }));
        return;
      }

      // Create a backup computer configuration
      const backupComputer: Computer = {
        model,
        type: computerDef.type,
        processing: computerDef.processing,
        techLevel: computerDef.techLevel,
        cost: computerDef.baseCost,
        options: [],
        isPrimary: false,
        isBackup: true,
      };

      dispatch(setSectionBackupComputer({ sectionId, backupComputer }));
      // Clear error for this section
      setErrors(prev => ({ ...prev, [sectionId]: '' }));
    }
  };

  const handlePrimaryOptionChange = (sectionId: string, option: ComputerOption, checked: boolean) => {
    const currentSection = sectionComputers[sectionId];
    if (!currentSection || !currentSection.primary) return;

    const newOptions = checked
      ? [...currentSection.primaryOptions, option]
      : currentSection.primaryOptions.filter(opt => opt !== option);

    // Immediately update Redux state (like main ComputerSelector)
    const updatedPrimary: Computer = {
      ...currentSection.primary,
      options: newOptions,
    };

    dispatch(setSectionComputer({ sectionId, computer: updatedPrimary }));
  };

  const handleBackupOptionChange = (sectionId: string, option: ComputerOption, checked: boolean) => {
    const currentSection = sectionComputers[sectionId];
    if (!currentSection || !currentSection.backup) return;

    const newOptions = checked
      ? [...currentSection.backupOptions, option]
      : currentSection.backupOptions.filter(opt => opt !== option);

    // Immediately update Redux state (like main ComputerSelector)
    const updatedBackup: Computer = {
      ...currentSection.backup,
      options: newOptions,
    };

    dispatch(setSectionBackupComputer({ sectionId, backupComputer: updatedBackup }));
  };

  // Single computer mode handlers (for main ship computer)
  const handleSinglePrimaryModelChange = (model: ComputerModel) => {
    const computerDef = COMPUTERS[model];
    const currentOptions = currentComputer?.primary?.options || [];

    // Check if new primary computer is smaller than existing backup computer
    if (currentComputer?.backup) {
      const newPrimaryProcessing = computerDef.processing;
      const backupProcessing = currentComputer.backup.processing;

      if (newPrimaryProcessing <= backupProcessing) {
        setErrors(prev => ({ 
          ...prev, 
          'single': `Primary computer (${newPrimaryProcessing} processing) must be larger than backup computer (${backupProcessing} processing)` 
        }));
        return;
      }
    }

    const newPrimary: Computer = {
      model,
      type: computerDef.type,
      processing: computerDef.processing,
      techLevel: computerDef.techLevel,
      cost: computerDef.baseCost,
      options: currentOptions,
      isPrimary: true,
      isBackup: false,
    };

    dispatch(setPrimaryComputer(newPrimary));
    setErrors(prev => ({ ...prev, 'single': '' }));
  };

  const handleSingleBackupModelChange = (model: ComputerModel | "") => {
    if (model === "") {
      dispatch(setBackupComputer(null));
      setErrors(prev => ({ ...prev, 'single': '' }));
    } else {
      if (!currentComputer?.primary) {
        setErrors(prev => ({ ...prev, 'single': 'Primary computer must be selected before backup computer' }));
        return;
      }

      const computerDef = COMPUTERS[model];
      const primaryProcessing = currentComputer.primary.processing;
      const backupProcessing = computerDef.processing;

      if (backupProcessing >= primaryProcessing) {
        setErrors(prev => ({ 
          ...prev, 
          'single': `Backup computer (${backupProcessing} processing) must be smaller than primary computer (${primaryProcessing} processing)` 
        }));
        return;
      }

      const backupComputer: Computer = {
        model,
        type: computerDef.type,
        processing: computerDef.processing,
        techLevel: computerDef.techLevel,
        cost: computerDef.baseCost,
        options: [],
        isPrimary: false,
        isBackup: true,
      };

      dispatch(setBackupComputer(backupComputer));
      setErrors(prev => ({ ...prev, 'single': '' }));
    }
  };

  const handleSinglePrimaryOptionChange = (option: ComputerOption, checked: boolean) => {
    if (!currentComputer?.primary) return;

    const newOptions = checked
      ? [...(currentComputer.primary.options || []), option]
      : (currentComputer.primary.options || []).filter(opt => opt !== option);

    const updatedPrimary: Computer = {
      ...currentComputer.primary,
      options: newOptions,
    };

    dispatch(setPrimaryComputer(updatedPrimary));
  };

  const handleSingleBackupOptionChange = (option: ComputerOption, checked: boolean) => {
    if (!currentComputer?.backup) return;

    const newOptions = checked
      ? [...(currentComputer.backup.options || []), option]
      : (currentComputer.backup.options || []).filter(opt => opt !== option);

    const updatedBackup: Computer = {
      ...currentComputer.backup,
      options: newOptions,
    };

    dispatch(setBackupComputer(updatedBackup));
  };


  const getTotalComputerCost = useCallback(() => {
    return Object.values(sectionComputers).reduce((sum, section) => {
      const primaryCost = section.primary?.cost || 0;
      const backupCost = section.backup?.cost || 0;
      
      // Calculate primary options cost based on multiplier
      const primaryOptionsCost = section.primaryOptions.reduce((optSum, opt) => {
        const option = COMPUTER_OPTIONS[opt];
        if (option && section.primary) {
          return optSum + (section.primary.cost * (option.costMultiplier - 1));
        }
        return optSum;
      }, 0);
      
      // Calculate backup options cost based on multiplier
      const backupOptionsCost = section.backupOptions.reduce((optSum, opt) => {
        const option = COMPUTER_OPTIONS[opt];
        if (option && section.backup) {
          return optSum + (section.backup.cost * (option.costMultiplier - 1));
        }
        return optSum;
      }, 0);
      
      return sum + primaryCost + backupCost + primaryOptionsCost + backupOptionsCost;
    }, 0);
  }, [sectionComputers]);

  const getTotalPrimaryProcessing = useCallback(() => {
    return Object.values(sectionComputers).reduce((sum, section) => {
      return sum + (section.primary?.processing || 0);
    }, 0);
  }, [sectionComputers]);

  const getTotalBackupProcessing = useCallback(() => {
    return Object.values(sectionComputers).reduce((sum, section) => {
      return sum + (section.backup?.processing || 0);
    }, 0);
  }, [sectionComputers]);

  const handleSave = useCallback(() => {
    // Check for errors
    const hasErrors = Object.values(errors).some(error => error !== '');
    if (hasErrors) {
      return;
    }

    // Find the most common primary computer type, or use the first one
    const primaryModels = Object.values(sectionComputers)
      .map(section => section.primary?.model)
      .filter(Boolean) as ComputerModel[];
    
    const mostCommonPrimary = primaryModels.length > 0 
      ? primaryModels.reduce((a, b) => 
          primaryModels.filter(v => v === a).length >= primaryModels.filter(v => v === b).length ? a : b
        )
      : 'Computer/10' as ComputerModel;

    // Find the most common backup computer type, or use the first one
    const backupModels = Object.values(sectionComputers)
      .map(section => section.backup?.model)
      .filter(Boolean) as ComputerModel[];
    
    const mostCommonBackup = backupModels.length > 0 
      ? backupModels.reduce((a, b) => 
          backupModels.filter(v => v === a).length >= backupModels.filter(v => v === b).length ? a : b
        )
      : null;

    // Combine all primary options
    const allPrimaryOptions = Object.values(sectionComputers)
      .flatMap(section => section.primaryOptions)
      .filter((option, index, array) => array.indexOf(option) === index); // Remove duplicates

    // Combine all backup options
    const allBackupOptions = Object.values(sectionComputers)
      .flatMap(section => section.backupOptions)
      .filter((option, index, array) => array.indexOf(option) === index); // Remove duplicates

    // Create combined primary computer
    if (mostCommonPrimary) {
      const primaryDef = COMPUTERS[mostCommonPrimary];
      const combinedPrimary: Computer = {
        model: mostCommonPrimary,
        type: primaryDef.type,
        processing: getTotalPrimaryProcessing(),
        techLevel: primaryDef.techLevel,
        cost: getTotalComputerCost(),
        options: allPrimaryOptions,
        isPrimary: true,
        isBackup: false,
      };
      dispatch(setPrimaryComputer(combinedPrimary));
    }

    // Create combined backup computer
    if (mostCommonBackup) {
      const backupDef = COMPUTERS[mostCommonBackup];
      const combinedBackup: Computer = {
        model: mostCommonBackup,
        type: backupDef.type,
        processing: getTotalBackupProcessing(),
        techLevel: backupDef.techLevel,
        cost: 0, // Backup cost is included in primary
        options: allBackupOptions,
        isPrimary: false,
        isBackup: true,
      };
      dispatch(setBackupComputer(combinedBackup));
    } else {
      dispatch(setBackupComputer(null));
    }

    // Save each section's computer configuration to Redux state
    Object.entries(sectionComputers).forEach(([sectionId, sectionComputer]) => {
      if (sectionComputer.primary) {
        // Create a Computer object for this section (using primary computer)
        const sectionComputerObj: Computer = {
          model: sectionComputer.primary.model,
          type: sectionComputer.primary.type,
          processing: sectionComputer.primary.processing,
          techLevel: sectionComputer.primary.techLevel,
          cost: sectionComputer.primary.cost,
          options: sectionComputer.primaryOptions,
          isPrimary: true,
          isBackup: false,
        };
        dispatch(setSectionComputer({ sectionId, computer: sectionComputerObj }));
      } else {
        dispatch(setSectionComputer({ sectionId, computer: null }));
      }
    });

    if (!inline) {
      onClose();
    }
  }, [inline, onClose, errors, sectionComputers, dispatch, getTotalPrimaryProcessing, getTotalComputerCost, getTotalBackupProcessing]);


  if (!isOpen || !breakawayHull?.sections) return null;

  const totalComputerCost = getTotalComputerCost();
  const totalPrimaryProcessing = getTotalPrimaryProcessing();
  const totalBackupProcessing = getTotalBackupProcessing();

  const content = (
    <div className={inline ? "w-full" : "bg-white dark:bg-gray-800 rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto"}>
      {!inline && (
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Breakaway Computer Configuration
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Computer Mode Toggle */}
      <div className="mb-6">
        <div className="flex items-center space-x-4">
          <span className="text-sm font-medium text-gray-700">Computer Configuration:</span>
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setComputerMode('single')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                computerMode === 'single'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Single Computer (Whole Ship)
            </button>
            <button
              onClick={() => setComputerMode('per-section')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                computerMode === 'per-section'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Computer per Section
            </button>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          {computerMode === 'single' 
            ? 'One computer system for the entire ship (standard Traveller approach)'
            : 'Individual computer systems for each breakaway section'
          }
        </p>
      </div>



      {/* Computer Configuration based on mode */}
      {computerMode === 'single' ? (
        /* Single Computer Configuration */
        <div className="space-y-6">
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Ship Computer System</h3>
            
            {/* Primary Computer */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Primary Computer
              </label>
              <select
                value={currentComputer?.primary?.model || ""}
                onChange={(e) => handleSinglePrimaryModelChange(e.target.value as ComputerModel)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
              >
                <option value="">Select Primary Computer</option>
                {Object.entries(COMPUTERS).map(([key, config]) => (
                  <option key={key} value={key}>
                    {config.model} (TL{config.techLevel})
                  </option>
                ))}
              </select>

              {/* Primary Computer Options */}
              {currentComputer?.primary && (
                <div className="mt-3">
                  <label className="block text-xs font-medium text-gray-600 mb-2">
                    Primary Options:
                  </label>
                  <div className="space-y-1">
                    {Object.entries(COMPUTER_OPTIONS).map(([key, option]) => (
                      <label key={key} className="flex items-center text-sm">
                        <input
                          type="checkbox"
                          checked={(currentComputer.primary?.options || []).includes(key as ComputerOption)}
                          onChange={(e) => handleSinglePrimaryOptionChange(key as ComputerOption, e.target.checked)}
                          className="mr-2"
                        />
                        {option.name}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Backup Computer */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Backup Computer (Optional)
              </label>
              <select
                value={currentComputer?.backup?.model || ""}
                onChange={(e) => handleSingleBackupModelChange(e.target.value as ComputerModel)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
              >
                <option value="">No Backup</option>
                {Object.entries(COMPUTERS).map(([key, config]) => {
                  const primaryProcessing = currentComputer?.primary?.processing || 0;
                  const backupProcessing = config.processing;
                  const isValid = primaryProcessing > 0 && backupProcessing < primaryProcessing;
                  
                  return (
                    <option 
                      key={key} 
                      value={key}
                      disabled={!isValid}
                    >
                      {config.model} (TL{config.techLevel}) {!isValid && primaryProcessing > 0 ? `(too large - primary is ${primaryProcessing})` : ''}
                    </option>
                  );
                })}
              </select>

              {/* Backup Computer Options */}
              {currentComputer?.backup && (
                <div className="mt-3">
                  <label className="block text-xs font-medium text-gray-600 mb-2">
                    Backup Options:
                  </label>
                  <div className="space-y-1">
                    {Object.entries(COMPUTER_OPTIONS).map(([key, option]) => (
                      <label key={key} className="flex items-center text-sm">
                        <input
                          type="checkbox"
                          checked={(currentComputer.backup?.options || []).includes(key as ComputerOption)}
                          onChange={(e) => handleSingleBackupOptionChange(key as ComputerOption, e.target.checked)}
                          className="mr-2"
                        />
                        {option.name}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Error Display */}
            {errors['single'] && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{errors['single']}</p>
              </div>
            )}

            {/* Computer Details */}
            <div className="mt-4 p-3 bg-white border border-gray-200 rounded-md">
              <h4 className="text-sm font-semibold text-gray-900 mb-2">Computer Details:</h4>
              <div className="text-sm text-gray-600 space-y-1">
                <div>Primary: {currentComputer?.primary?.processing || 0} processing</div>
                <div>Primary Cost: {formatCurrency((currentComputer?.primary?.cost || 0) * 1000000)}</div>
                <div>Backup: {currentComputer?.backup?.processing || 0} processing</div>
                <div>Backup Cost: {formatCurrency((currentComputer?.backup?.cost || 0) * 1000000)}</div>
              </div>
              <div className="mt-2 text-sm text-green-600">
                ✓ Computer adequate for ship requirements
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Per-Section Computer Configuration */
        <div className="space-y-6">
        {breakawayHull.sections.map((section) => {
          const sectionComputer = sectionComputers[section.id] || {
            sectionId: section.id,
            primary: null,
            backup: null,
            primaryOptions: [],
            backupOptions: [],
            isAdequate: false,
          };
          const error = errors[section.id];

          return (
            <div key={section.id} className="border border-gray-200 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-3">
                {section.name} ({section.size} tons)
              </h4>

              {error && (
                <div className="text-red-600 text-sm mb-3 p-2 bg-red-50 border border-red-200 rounded">
                  {error}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Primary Computer */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Primary Computer
                  </label>
                                     <select
                     value={sectionComputer.primary?.model || ""}
                     onChange={(e) => handlePrimaryModelChange(section.id, e.target.value as ComputerModel)}
                     className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                   >
                     <option value="">Select Primary Computer</option>
                     {Object.entries(COMPUTERS).map(([key, config]) => (
                       <option key={key} value={key}>
                         {config.model} (TL{config.techLevel})
                       </option>
                     ))}
                   </select>

                  {/* Primary Computer Options */}
                  {sectionComputer.primary && (
                    <div className="mt-2">
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Primary Options
                      </label>
                      <div className="space-y-1">
                        {Object.entries(COMPUTER_OPTIONS).map(([key, option]) => (
                          <label key={key} className="flex items-center text-xs">
                            <input
                              type="checkbox"
                              checked={sectionComputer.primaryOptions.includes(key as ComputerOption)}
                              onChange={(e) => handlePrimaryOptionChange(section.id, key as ComputerOption, e.target.checked)}
                              className="mr-1"
                            />
                            {option.name}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Backup Computer */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Backup Computer (Optional)
                  </label>
                  <select
                    value={sectionComputer.backup?.model || ""}
                    onChange={(e) => handleBackupModelChange(section.id, e.target.value as ComputerModel)}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                  >
                    <option value="">No Backup</option>
                    {Object.entries(COMPUTERS).map(([key, config]) => {
                      const currentSection = sectionComputers[section.id];
                      const primaryProcessing = currentSection?.primary?.processing || 0;
                      const backupProcessing = config.processing;
                      const isValid = primaryProcessing > 0 && backupProcessing < primaryProcessing;
                      
                      return (
                        <option 
                          key={key} 
                          value={key}
                          disabled={!isValid}
                        >
                          {config.model} (TL{config.techLevel}) {!isValid && primaryProcessing > 0 ? `(too large - primary is ${primaryProcessing})` : ''}
                        </option>
                      );
                    })}
                  </select>

                  {/* Backup Computer Options */}
                  {sectionComputer.backup && (
                    <div className="mt-2">
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Backup Options
                      </label>
                      <div className="space-y-1">
                        {Object.entries(COMPUTER_OPTIONS).map(([key, option]) => (
                          <label key={key} className="flex items-center text-xs">
                            <input
                              type="checkbox"
                              checked={sectionComputer.backupOptions.includes(key as ComputerOption)}
                              onChange={(e) => handleBackupOptionChange(section.id, key as ComputerOption, e.target.checked)}
                              className="mr-1"
                            />
                            {option.name}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Computer Details */}
              <div className="mt-3 text-sm text-gray-600">
                <div className="mb-2">
                  <strong>Computer Details:</strong>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>Primary: {sectionComputer.primary?.processing || 0} processing</div>
                  <div>Backup: {sectionComputer.backup?.processing || 0} processing</div>
                  <div>Primary Cost: {formatCurrency(sectionComputer.primary?.cost || 0)}</div>
                  <div>Backup Cost: {formatCurrency(sectionComputer.backup?.cost || 0)}</div>
                </div>
                {sectionComputer.primaryOptions.length > 0 && (
                  <div className="mt-2 text-xs text-purple-600">
                    <strong>Primary Options:</strong> {sectionComputer.primaryOptions.map(opt => COMPUTER_OPTIONS[opt].name).join(", ")}
                  </div>
                )}
                {sectionComputer.backupOptions.length > 0 && (
                  <div className="mt-2 text-xs text-purple-600">
                    <strong>Backup Options:</strong> {sectionComputer.backupOptions.map(opt => COMPUTER_OPTIONS[opt].name).join(", ")}
                  </div>
                )}
              </div>

              {/* Adequacy Status */}
              <div className={`mt-2 text-sm font-medium ${sectionComputer.isAdequate ? 'text-green-600' : 'text-red-600'}`}>
                {sectionComputer.isAdequate ? '✓ Computer adequate for section requirements' : '✗ Computer inadequate for section requirements'}
              </div>
            </div>
          );
        })}
        </div>
      )}

      {/* Summary - Only show for per-section mode */}
      {computerMode === 'per-section' && (
      <div className="mt-6 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Combined Computer Summary</h4>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-600 dark:text-gray-300">Total Computer Cost:</span>
            <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">{formatCurrency(totalComputerCost)}</span>
          </div>
          <div>
            <span className="text-gray-600 dark:text-gray-300">Total Primary Processing:</span>
            <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">{formatNumber(totalPrimaryProcessing)}</span>
          </div>
          <div>
            <span className="text-gray-600 dark:text-gray-300">Total Backup Processing:</span>
            <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">{formatNumber(totalBackupProcessing)}</span>
          </div>
          <div>
            <span className="text-gray-600 dark:text-gray-300">Effective Processing:</span>
            <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">{formatNumber(Math.max(totalPrimaryProcessing, totalBackupProcessing))}</span>
          </div>
        </div>
      </div>
      )}

      {/* General Errors */}
      {errors.general && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-700">{errors.general}</p>
        </div>
      )}

      {!inline && (
        <div className="flex justify-end mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 mr-3"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={Object.values(errors).some(error => error !== '')}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Save Configuration
          </button>
        </div>
      )}
    </div>
  );

  if (inline) {
    return content;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      {content}
    </div>
  );
}; 